import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { AppTooltip } from '@/components/ui/app-tooltip';
import { Icon, type IconProps } from '@/components/ui/icon';
import { Chart01Icon, Clock01Icon, Coins01Icon, UserIcon, UserMultiple02Icon, UserMultipleIcon } from '@/components/ui/icon/glyphs';
import { cn } from '@/lib/ui/utils';
import { isLopsidedShare } from './concentration-warning';
import type { ComparisonMetric, IntegrityComparison } from './resolution-integrity-types';
import { useMetricFormat } from './use-metric-format';
import { useMetricLabel } from './use-metric-label';
import { WarningMarker } from './warning-marker';
const METRIC_ICON: Record<ComparisonMetric, IconProps['icon']> = {
  averagePosition: Coins01Icon,
  holders: UserMultiple02Icon,
  volume: Chart01Icon,
  largestHolderShare: UserIcon,
  top5HoldersShare: UserMultipleIcon,
  finalHourNetInflow: Clock01Icon
};
export function ComparisonCards(t0) {
  const $ = _c(4);
  const {
    comparisons
  } = t0;
  let t1;
  if ($[0] !== comparisons) {
    t1 = comparisons.map(_temp);
    $[0] = comparisons;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="grid min-w-0 flex-1 grid-cols-2 gap-2 lg:grid-cols-3">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(comparison) {
  return <ComparisonCard key={comparison.metric} comparison={comparison} />;
}
function ComparisonCard(t0) {
  const $ = _c(44);
  const {
    comparison
  } = t0;
  const t = useTranslations("market.resolutionIntegrity");
  const format = useMetricFormat();
  const label = useMetricLabel();
  const signed = comparison.metric === "finalHourNetInflow";
  let t1;
  if ($[0] !== comparison.metric || $[1] !== comparison.winner) {
    t1 = comparison.metric === "largestHolderShare" && isLopsidedShare(comparison.winner === null ? null : Number(comparison.winner));
    $[0] = comparison.metric;
    $[1] = comparison.winner;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const lopsided = t1;
  const t2 = comparison.metric;
  const t3 = comparison.flagged ? "true" : undefined;
  const t4 = lopsided ? "true" : undefined;
  const t5 = METRIC_ICON[comparison.metric];
  let t6;
  if ($[3] !== t5) {
    t6 = <Icon icon={t5} aria-hidden={true} className="size-4 shrink-0" />;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== comparison.metric || $[6] !== label) {
    t7 = label(comparison.metric);
    $[5] = comparison.metric;
    $[6] = label;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t7) {
    t8 = <span className="line-clamp-2">{t7}</span>;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t6 || $[11] !== t8) {
    t9 = <span className="flex items-center gap-1.5 text-caption text-foreground-tertiary">{t6}{t8}</span>;
    $[10] = t6;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== comparison.flagged || $[14] !== comparison.metric || $[15] !== lopsided) {
    t10 = comparison.flagged || lopsided ? <WarningMarker metric={comparison.metric} flagged={comparison.flagged} /> : null;
    $[13] = comparison.flagged;
    $[14] = comparison.metric;
    $[15] = lopsided;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = comparison.flagged ? "text-feedback-warning-content" : "text-foreground-primary";
  let t12;
  if ($[17] !== t11) {
    t12 = cn("text-body-lg font-medium tabular-nums", t11);
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== comparison.unit || $[20] !== comparison.winner || $[21] !== format || $[22] !== signed) {
    t13 = format.byUnit(comparison.winner, comparison.unit, signed);
    $[19] = comparison.unit;
    $[20] = comparison.winner;
    $[21] = format;
    $[22] = signed;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t12 || $[25] !== t13) {
    t14 = <span className={t12}>{t13}</span>;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== comparison.baseline || $[28] !== comparison.metric || $[29] !== comparison.unit || $[30] !== format || $[31] !== signed || $[32] !== t) {
    t15 = comparison.baseline === null ? null : <AppTooltip content={comparison.metric === "averagePosition" ? t("baselinePooledHint") : t("baselineHint")} side="top"><button type="button" data-testid="resolution-integrity-baseline" className="cursor-help text-caption tabular-nums text-foreground-secondary underline decoration-dotted decoration-foreground-tertiary underline-offset-2">{t("versus", {
          value: format.byUnit(comparison.baseline, comparison.unit, signed)
        })}</button></AppTooltip>;
    $[27] = comparison.baseline;
    $[28] = comparison.metric;
    $[29] = comparison.unit;
    $[30] = format;
    $[31] = signed;
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t10 || $[35] !== t14 || $[36] !== t15) {
    t16 = <span className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">{t10}{t14}{t15}</span>;
    $[34] = t10;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== comparison.metric || $[39] !== t16 || $[40] !== t3 || $[41] !== t4 || $[42] !== t9) {
    t17 = <div data-testid="resolution-integrity-card" data-metric={t2} data-flagged={t3} data-lopsided={t4} className="flex min-w-0 flex-col gap-1 rounded-[calc(var(--radius-card)-0.25rem)] bg-background-tertiary/50 px-3 py-3">{t9}{t16}</div>;
    $[38] = comparison.metric;
    $[39] = t16;
    $[40] = t3;
    $[41] = t4;
    $[42] = t9;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  return t17;
}